import React from 'react';
import { FolderOpen, MonitorDown, BarChart3 } from 'lucide-react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import { SolutionIndustriesConvergence } from './SolutionIndustriesConvergence';
import type { SolutionFlowCard, SolutionPremiumSections } from './types';

const CARD_THEMES = ['rose', 'sky', 'teal', 'violet'] as const;

const FLOW_LEFT = 'M 250 0 C 250 130, 205 220, 500 340';
const FLOW_RIGHT = 'M 750 0 C 750 130, 795 220, 500 340';
const FLOW_TAIL = 'M 500 340 L 500 390';

const FLOW_ICONS = {
  Provision: FolderOpen,
  Connect: MonitorDown,
  Integrate: BarChart3,
} as const;

function FlowCardIcon({ card }: { card: SolutionFlowCard }) {
  const Icon = FLOW_ICONS[card.title as keyof typeof FLOW_ICONS] ?? FolderOpen;
  return <Icon size={18} strokeWidth={1.75} aria-hidden />;
}

export const SolutionFloorAutomation: React.FC<{
  verticals: SolutionPremiumSections['verticals'];
}> = ({ verticals }) => {
  if (verticals.sceneImage && verticals.convergence) {
    return <SolutionIndustriesConvergence verticals={verticals} />;
  }

  const isIotPremium = verticals.items.some((item) => (item.featureChips?.length ?? 0) > 0);

  return (
  <SiteSection
    id="offerings"
    className={`solution-floor-auto${isIotPremium ? ' solution-floor-auto--iot-premium' : ''}`}
  >
    <SiteSectionHeader
      eyebrow={verticals.eyebrow}
      title={verticals.title}
      subtitle={verticals.subtitle}
      align="center"
    />

    <div className="solution-floor-auto__grid">
      {verticals.items.map((item, index) => {
        const theme = item.cardTheme ?? CARD_THEMES[index % CARD_THEMES.length];
        return (
          <article
            key={item.id}
            className={`solution-floor-auto__card solution-floor-auto__card--${theme}`}
            style={{ '--card-i': index } as React.CSSProperties}
          >
            <div className="solution-floor-auto__art" aria-hidden>
              <img
                src={item.image.src}
                alt=""
                width={item.image.width}
                height={item.image.height}
                loading="lazy"
              />
            </div>
            <div className="solution-floor-auto__card-body">
              <h3 className="solution-floor-auto__card-title">{item.label}</h3>
              {item.subtitle ? (
                <p className="solution-floor-auto__card-subtitle">{item.subtitle}</p>
              ) : null}
              <ul className="solution-floor-auto__list">
                {item.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              {item.featureChips?.length ? (
                <div className="solution-floor-auto__chips">
                  <p className="solution-floor-auto__chips-label">IoT Capabilities</p>
                  <div className="solution-floor-auto__chips-row">
                    {item.featureChips.map((chip) => (
                      <span key={chip} className="solution-floor-auto__chip">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>

    {verticals.hub ? (
      <div className="solution-floor-auto__bridge" aria-hidden>
        <svg className="solution-floor-auto__flow-svg" viewBox="0 0 1000 400" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="solution-flow-tail" x1="0" y1="0" x2="1000" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#f25f5c" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
          </defs>
          <path
            className="solution-floor-auto__flow-line solution-floor-auto__flow-line--left"
            d={FLOW_LEFT}
            fill="none"
            strokeWidth="4"
          />
          <path
            className="solution-floor-auto__flow-line solution-floor-auto__flow-line--right"
            d={FLOW_RIGHT}
            fill="none"
            strokeWidth="4"
          />
          <path
            className="solution-floor-auto__flow-line solution-floor-auto__flow-line--tail"
            d={FLOW_TAIL}
            fill="none"
            strokeWidth="4"
            stroke="url(#solution-flow-tail)"
          />
          <circle className="solution-floor-auto__flow-ball solution-floor-auto__flow-ball--left" r="11">
            <animateMotion
              dur="4.4s"
              repeatCount="indefinite"
              path={FLOW_LEFT}
              keyTimes="0;1"
              calcMode="spline"
              keySplines="0.42 0 0.58 1"
            />
          </circle>
          <circle className="solution-floor-auto__flow-ball solution-floor-auto__flow-ball--right" r="11">
            <animateMotion
              dur="4.4s"
              repeatCount="indefinite"
              path={FLOW_RIGHT}
              begin="0.55s"
              keyTimes="0;1"
              calcMode="spline"
              keySplines="0.42 0 0.58 1"
            />
          </circle>
        </svg>

        <div className="solution-floor-auto__hub">
          {verticals.hub.flowCards.map((card) => (
            <div
              key={card.title}
              className={`solution-floor-auto__flow-card solution-floor-auto__flow-card--${card.tone} solution-floor-auto__flow-card--${card.position}`}
            >
              <span className="solution-floor-auto__flow-card-icon">
                <FlowCardIcon card={card} />
              </span>
              <p className="solution-floor-auto__flow-card-title">{card.title}</p>
              <p className="solution-floor-auto__flow-card-desc">{card.description}</p>
            </div>
          ))}

          <div className="solution-floor-auto__devices">
            {verticals.hub.phoneImage ? (
              <img
                className="solution-floor-auto__phone"
                src={verticals.hub.phoneImage.src}
                alt=""
                width={verticals.hub.phoneImage.width}
                height={verticals.hub.phoneImage.height}
                loading="lazy"
              />
            ) : null}
            <img
              className="solution-floor-auto__laptop"
              src={verticals.hub.laptopImage.src}
              alt={verticals.hub.laptopImage.alt}
              width={verticals.hub.laptopImage.width}
              height={verticals.hub.laptopImage.height}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    ) : isIotPremium ? null : (
      <div className="solution-floor-auto__connectors" aria-hidden>
        <svg className="solution-floor-auto__connector solution-floor-auto__connector--left" viewBox="0 0 120 80">
          <path d="M60 0 C60 40 20 50 10 80" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
        <span className="solution-floor-auto__node" />
        <svg className="solution-floor-auto__connector solution-floor-auto__connector--right" viewBox="0 0 120 80">
          <path d="M60 0 C60 40 100 50 110 80" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>
    )}
  </SiteSection>
  );
};
